import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useScrollTo } from "@/lib/smooth-scroll";
import { LOGO_URL } from "@/data";

const LINKS = [
  { label: "Formules", target: "#formules" },
  { label: "Réalisations", target: "#realisations" },
  { label: "Le Process", target: "#process" },
  { label: "Contact", target: "#contact" },
];

export const Navbar = () => {
  const scrollTo = useScrollTo();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (t) => {
    setOpen(false);
    scrollTo(t);
  };

  return (
    <motion.header
      data-testid="main-navigation"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-5 sm:px-8 h-[70px] flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#hero")}
          className="flex items-center"
        >
          <img
            src={LOGO_URL}
            alt="Speed Auto Lavage"
            className="h-11 sm:h-12 w-auto object-contain mix-blend-screen"
          />
        </button>

        <div className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-link-${l.target.slice(1)}`}
              onClick={() => go(l.target)}
              className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-[var(--txt-1)] hover:text-white transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </div>

        <button
          data-testid="nav-reserve-btn"
          onClick={() => go("#reservation")}
          className="hidden md:inline-flex items-center gap-2 bg-white text-black font-mono2 text-[11px] font-bold uppercase tracking-[0.18em] px-5 py-2.5 rounded-sm hover:bg-[var(--blue)] hover:text-white transition-colors duration-300"
        >
          Réserver
        </button>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden text-white p-2"
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.target}
                  data-testid={`nav-mobile-link-${l.target.slice(1)}`}
                  onClick={() => go(l.target)}
                  className="text-left font-display text-lg text-white"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-reserve"
                onClick={() => go("#reservation")}
                className="mt-2 bg-[var(--blue)] text-white font-mono2 text-xs font-bold uppercase tracking-[0.18em] px-5 py-3 rounded-sm"
              >
                Réserver maintenant
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
